import dayjs, { type Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';

// 扩展 dayjs 插件
dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

/**
 * 时间处理工具函数
 */

/** 列表页日期时间范围选择器统一格式 */
export const DATE_TIME_RANGE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

/**
 * 日期范围选择：选结束日期时若时间仍为 00:00:00，自动设为当天 23:59:59。
 * 与任务列表、客户列表等页面的 RangePicker onCalendarChange 行为一致。
 */
export function normalizeDateRangeEndOnCalendarChange(
  dates: [Dayjs | null, Dayjs | null] | null,
): void {
  if (dates?.[1]) {
    const timeStr = dates[1].format('HH:mm:ss');
    if (timeStr === '00:00:00') {
      dates[1] = dates[1].endOf('day');
    }
  }
}

/**
 * 格式化时间戳为日期字符串
 * @param timestamp 时间戳（支持字符串和数字格式）
 * @param format 格式化模板，默认为 'YYYY-MM-DD'
 * @returns 格式化后的日期字符串，如果无效则返回 '-'
 */
export const formatTimestamp = (timestamp: string | number | null | undefined, format: string = 'YYYY-MM-DD'): string => {
  if (!timestamp && timestamp !== 0) return '-';

  // ISO / 日期字符串（如 2026-08-13T11:05:39）不可 parseInt，否则会得到 2026→1970
  if (typeof timestamp === 'string' && !/^\d+$/.test(timestamp.trim())) {
    const date = dayjs(timestamp);
    return date.isValid() ? date.format(format) : '-';
  }

  const numTimestamp = typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp;
  if (isNaN(numTimestamp)) return '-';

  const date = dayjs(numTimestamp);
  if (!date.isValid()) return '-';

  return date.format(format);
};

/**
 * 格式化时间戳为中文日期格式
 * @param timestamp 时间戳（支持字符串和数字格式）
 * @returns 中文格式的日期字符串，如 '2025-01-30'
 */
export const formatTimestampToChinese = (timestamp: string | number | null | undefined): string => {
  return formatTimestamp(timestamp, 'YYYY-MM-DD');
};

/**
 * 格式化时间戳为完整的中文日期时间格式
 * @param timestamp 时间戳（支持字符串和数字格式）
 * @returns 完整的中文日期时间字符串，如 '2025-01-30 14:30:00'
 */
export const formatTimestampToFullChinese = (timestamp: string | number | null | undefined): string => {
  return formatTimestamp(timestamp, 'YYYY-MM-DD HH:mm:ss');
};

/**
 * 格式化时间戳为相对时间
 * @param timestamp 时间戳（支持字符串和数字格式）
 * @returns 相对时间字符串，如 '2天前'、'1个月前'
 */
export const formatTimestampToRelative = (timestamp: string | number | null | undefined): string => {
  if (!timestamp && timestamp !== 0) return '-';

  const date =
    typeof timestamp === 'string' && !/^\d+$/.test(timestamp.trim())
      ? dayjs(timestamp)
      : dayjs(typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp);

  if (!date.isValid()) return '-';

  return date.fromNow();
};

/**
 * 格式化ISO日期字符串为完整的中文日期时间格式
 * @param dateString ISO日期字符串（如 "2025-10-13T14:23:02Z"）
 * @returns 完整的中文日期时间字符串，如 '2025-10-13 14:23:02'
 */
export const formatDateStringToFullChinese = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  const date = dayjs(dateString);

  if (!date.isValid()) return '-';

  return date.format('YYYY-MM-DD HH:mm:ss');
};

/**
 * 格式化ISO日期字符串为中文日期格式（只显示日期，不显示时间）
 * @param dateString ISO日期字符串（如 "2025-10-13T14:23:02Z"）
 * @returns 中文日期字符串，如 '2025-10-13'
 */
export const formatDateStringToChinese = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  const date = dayjs(dateString);

  if (!date.isValid()) return '-';

  return date.format('YYYY-MM-DD');
};

/**
 * 将时间戳转换为 dayjs 对象
 * @param timestamp 时间戳（支持字符串和数字格式）
 * @returns dayjs 对象，如果无效则返回 undefined
 */
export const timestampToDayjs = (timestamp: string | number | null | undefined): dayjs.Dayjs | undefined => {
  if (!timestamp) return undefined;

  // 处理字符串格式的时间戳
  const numTimestamp = typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp;

  // 检查是否为有效数字
  if (isNaN(numTimestamp)) return undefined;

  // 使用 dayjs 转换
  const date = dayjs(numTimestamp);

  // 检查日期是否有效
  if (!date.isValid()) return undefined;

  return date;
};

/**
 * 列表页时间列统一展示：支持 ISO 字符串（含 `T`）、毫秒/秒级时间戳数字或纯数字字符串。
 * 输出格式：`YYYY-MM-DD HH:mm:ss`，与任务列表等页面一致。
 */
export const formatDateTimeForTable = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'number') {
    const d = dayjs(value);
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : '-';
  }
  const s = String(value).trim();
  if (!s) return '-';
  if (/^\d+$/.test(s)) {
    const n = parseInt(s, 10);
    const d = dayjs(n < 1e12 ? n * 1000 : n);
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : '-';
  }
  const d = dayjs(s);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : '-';
};
